import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCart } from "@/lib/cart";
import { useConfig, money } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { IMG } from "@/lib/content";
import { SearchDialog } from "./SearchDialog";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useSeason } from "@/lib/holiday";
import { SantaHat, Garland } from "@/components/fx/Holiday";

const useNav = () => {
  const { t } = useI18n();
  const { active } = useSeason();
  return [
    ...(active ? [{ to: "/gifts", label: t("nav_gifts"), id: "gifts" }] : []),
    { to: "/shop", label: t("nav_shop_all"), id: "shop-all" },
    { to: "/collections/primus", label: "Primus", id: "primus" },
    { to: "/collections/bellecare", label: "Bellecare", id: "bellecare" },
    { to: "/sale", label: t("nav_sale"), id: "sale" },
    { to: "/blog", label: t("nav_journal"), id: "journal" },
    { to: "/b2b", label: "B2B", id: "b2b" },
  ];
};

const Announcement = () => {
  const { data } = useConfig();
  const { t } = useI18n();
  const { active, maxPct } = useSeason();
  const text = active ? t("announcement_holiday", { pct: maxPct }) : t("announcement", { amount: money(data?.free_shipping_threshold || 50), pct: data?.discount_percent || 10 });
  return (
    <div data-testid="announcement-bar" className="bg-gold text-[#0b0a08] text-[11px] tracking-[0.06em] uppercase font-semibold text-center py-2 px-4 overflow-hidden">
      <motion.p key={text} initial={{ y: 14, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>{text}</motion.p>
    </div>
  );
};

const CartButton = () => {
  const { count, setOpen } = useCart();
  return (
    <button data-testid="header-cart-button" onClick={() => setOpen(true)} className="relative text-[#e3cb94] hover:text-white transition-colors" aria-label="Cart">
      <ShoppingBag size={22} />
      <AnimatePresence>
        {count > 0 && (
          <motion.span key={count} initial={{ scale: 0 }} animate={{ scale: [1.6, 1] }} exit={{ scale: 0 }} data-testid="header-cart-count"
            className="absolute -top-2 -right-2 bg-gold text-[#0b0a08] text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">{count}</motion.span>
        )}
      </AnimatePresence>
    </button>
  );
};

export const Header = () => {
  const nav = useNav();
  const { season } = useSeason();
  const [scrolled, setScrolled] = useState(false);
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header className="sticky top-0 z-40">
      <Announcement />
      <div className={`transition-colors duration-500 ${scrolled ? "bg-[#0b0a08]/80 backdrop-blur-xl border-b border-white/5" : "bg-[#0b0a08]"}`}>
        <div className={`max-w-7xl mx-auto px-5 lg:px-10 flex items-center justify-between transition-[height] duration-500 ${scrolled ? "h-16" : "h-16 lg:h-20"}`}>
          <button data-testid="mobile-menu-toggle" className="lg:hidden text-[#e3cb94]" onClick={() => setMobile(!mobile)} aria-label="Menu">
            {mobile ? <X size={22} /> : <Menu size={22} />}
          </button>
          <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
            <span className="relative block h-12 w-12 lg:h-14 lg:w-14 transition-transform duration-700 group-hover:rotate-[8deg] group-hover:scale-110"><img src={IMG.logo} alt="AJS Luxury" className="h-full w-full object-contain" />{season && <SantaHat />}</span>
            <span className="hidden sm:block font-display text-lg tracking-[0.2em] text-[#e3cb94]">AJS LUXURY</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-10">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.id}`}
                className={({ isActive }) => `relative text-[12px] uppercase tracking-[0.14em] transition-colors after:absolute after:left-0 after:-bottom-1.5 after:h-px after:bg-[#c9a55c] after:transition-[width] after:duration-500 hover:after:w-full ${isActive ? "text-[#c9a55c] after:w-full" : "text-white/75 hover:text-white after:w-0"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-4 lg:gap-5">
            <div className="hidden lg:block"><LanguageSwitcher /></div>
            <SearchDialog />
            <CartButton />
          </div>
        </div>
        <Garland />
        <AnimatePresence>
          {mobile && (
            <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="lg:hidden border-t border-white/5 px-5 bg-[#0b0a08] overflow-hidden">
              <div className="py-5 flex flex-col gap-5">
                {nav.map((n, i) => (
                  <motion.div key={n.to} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: i * 0.06 }}>
                    <Link to={n.to} onClick={() => setMobile(false)} data-testid={`mobile-nav-${n.id}`} className="font-display text-2xl text-white/90">{n.label}</Link>
                  </motion.div>
                ))}
                <LanguageSwitcher testid="mobile-language-switcher" />
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};
